"use client";

import { useRouter } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { CornerDownLeft, Search } from "lucide-react";
import { useSession } from "@/components/session-provider";
import { useResource } from "@/lib/use-resource";
import { rankItems, type PaletteItem } from "@/lib/palette";
import type { Server } from "@/lib/types";

const serverTabs = [
  { tab: "console", label: "Console" },
  { tab: "files", label: "Files" },
  { tab: "monitoring", label: "Monitors" },
  { tab: "startup", label: "Startup" },
  { tab: "databases", label: "Databases" },
  { tab: "schedules", label: "Schedules" },
  { tab: "network", label: "Network" },
  { tab: "users", label: "Users" },
  { tab: "backups", label: "Backups" },
  { tab: "history", label: "File history" },
  { tab: "crash-doctor", label: "Crash doctor" },
  { tab: "plugin-check", label: "Plugin check" },
  { tab: "activity", label: "Activity" },
  { tab: "settings", label: "Settings" },
];

export const openPaletteEvent = "velnqo:open-palette";

export function CommandPalette() {
  const session = useSession();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const servers = useResource<{ servers: Server[] }>("/servers", 0, open && !!session.user);

  useEffect(() => {
    const reset = () => {
      setQuery("");
      setActive(0);
    };
    const toggle = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        reset();
        setOpen((value) => !value);
      }
    };
    const show = () => {
      reset();
      setOpen(true);
    };
    window.addEventListener("keydown", toggle);
    window.addEventListener(openPaletteEvent, show);
    return () => {
      window.removeEventListener("keydown", toggle);
      window.removeEventListener(openPaletteEvent, show);
    };
  }, []);

  useEffect(() => {
    if (open) input.current?.focus();
  }, [open]);

  const items = useMemo(() => {
    const out: PaletteItem[] = [
      { id: "page-servers", label: "Servers", hint: "Page", href: "/" },
      { id: "page-appearance", label: "Appearance", hint: "Page", href: "/settings/appearance" },
      { id: "page-account", label: "Account settings", hint: "Page", href: "/settings/account" },
      { id: "page-billing", label: "Billing", hint: "Page", href: "/billing" },
    ];
    if (session.can("marketplace.read")) out.push({ id: "page-marketplace", label: "Marketplace", hint: "Page", href: "/marketplace" });
    if (session.can("discord.manage")) out.push({ id: "page-discord", label: "Discord", hint: "Page", href: "/settings/discord" });
    if (session.tenant) out.push({ id: "page-support", label: "Live support", hint: "Page", href: "/support" });
    for (const server of servers.data?.servers ?? []) {
      const base = `/servers/${encodeURIComponent(server.shortId)}`;
      out.push({ id: `server-${server.id}`, label: server.name, hint: "Server", href: base, keywords: server.shortId });
      for (const { tab, label } of serverTabs) {
        out.push({
          id: `server-${server.id}-${tab}`,
          label: `${server.name}: ${label}`,
          hint: "Server tab",
          href: tab === "files" ? `${base}/files` : `${base}?tab=${tab}`,
        });
      }
    }
    return out;
  }, [session, servers.data]);

  const results = useMemo(() => rankItems(items, query), [items, query]);

  if (!open || !session.user) return null;

  const go = (item: PaletteItem | undefined) => {
    if (!item) return;
    setOpen(false);
    router.push(item.href);
  };

  return (
    <div className="palette-backdrop" onMouseDown={() => setOpen(false)}>
      <div className="palette" role="dialog" aria-modal="true" aria-label="Command palette" onMouseDown={(event) => event.stopPropagation()}>
        <div className="palette-input">
          <Search size={16} aria-hidden="true" />
          <input
            ref={input}
            role="combobox"
            aria-expanded="true"
            aria-controls="palette-results"
            aria-activedescendant={results[active] ? `palette-${results[active].id}` : undefined}
            aria-label="Search pages and servers"
            placeholder="Jump to a server, tab or page"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActive(0);
            }}
            onKeyDown={(event) => {
              if (event.key === "Escape") setOpen(false);
              else if (event.key === "ArrowDown") {
                event.preventDefault();
                setActive((value) => Math.min(value + 1, results.length - 1));
              } else if (event.key === "ArrowUp") {
                event.preventDefault();
                setActive((value) => Math.max(value - 1, 0));
              } else if (event.key === "Enter") {
                event.preventDefault();
                go(results[active]);
              }
            }}
          />
        </div>
        <ul id="palette-results" role="listbox" className="palette-list">
          {results.map((item, index) => (
            <li
              key={item.id}
              id={`palette-${item.id}`}
              role="option"
              aria-selected={index === active}
              className={index === active ? "palette-active" : undefined}
              onMouseMove={() => setActive(index)}
              onClick={() => go(item)}
            >
              <span>{item.label}</span>
              <small className="muted">{item.hint}</small>
            </li>
          ))}
          {results.length === 0 && <li className="palette-empty muted">{servers.loading ? "Loading servers..." : "No matches"}</li>}
        </ul>
        <div className="palette-foot muted">
          <span>↑↓ to move</span>
          <span>
            <CornerDownLeft size={12} aria-hidden="true" /> to open
          </span>
          <span>Esc to close</span>
        </div>
      </div>
    </div>
  );
}
